// src/NotifichePage.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Stack, Chip, CircularProgress, IconButton } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckIcon from '@mui/icons-material/Check';
import { fetchJson, authHeaders } from './api';

export default function NotifichePage({ token }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [notifiche, setNotifiche] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchJson('/alertsjobs/notifications', { headers: authHeaders(token) });
        setNotifiche(Array.isArray(data) ? data : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const markRead = async (id) => {
    try {
      await fetchJson(`/alertsjobs/notifications/${id}/read`, { method: 'POST', headers: authHeaders(token) });
      setNotifiche(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) { console.error(e); }
  };

  const unread = notifiche.filter(n => !n.read).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 700 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
        <NotificationsActiveIcon sx={{ color: '#d4a550', fontSize: 22 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0ece4' }}>
          {t('notifiche.title')}
        </Typography>
        {!loading && unread > 0 && (
          <Chip label={`${unread} ${t('notifiche.new_badge')}`} size="small"
            sx={{ bgcolor: 'rgba(212,165,80,0.15)', border: '1px solid rgba(212,165,80,0.4)', color: '#d4a550', fontWeight: 700 }} />
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#d4a550' }} />
        </Box>
      ) : notifiche.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <NotificationsNoneIcon sx={{ fontSize: 52, color: '#2a2a2a', mb: 2 }} />
          <Typography sx={{ color: '#555', fontSize: 14, whiteSpace: 'pre-line' }}>
            {t('notifiche.empty_text')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {notifiche.map(n => (
            <Box key={n.id} onClick={() => !n.read && markRead(n.id)}
              sx={{
                p: 2, borderRadius: 2, cursor: n.read ? 'default' : 'pointer',
                bgcolor: n.read ? '#0f1115' : '#13161c',
                border: `1px solid ${n.read ? 'rgba(255,255,255,0.04)' : 'rgba(212,165,80,0.2)'}`,
                transition: 'all 0.15s',
                '&:hover': !n.read ? { borderColor: 'rgba(212,165,80,0.4)' } : {}
              }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box sx={{ mt: 0.3, width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: n.read ? 'transparent' : '#d4a550' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: n.read ? 400 : 600, color: n.read ? '#666' : '#f0ece4', fontSize: 14 }}>
                    {n.title}
                  </Typography>
                  <Typography sx={{ color: '#666', fontSize: 12, mt: 0.3 }}>{n.body}</Typography>
                  <Typography sx={{ color: '#444', fontSize: 11, mt: 0.5 }}>
                    {new Date(n.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                {!n.read && (
                  <IconButton size="small" onClick={e => { e.stopPropagation(); markRead(n.id); }}
                    sx={{ color: '#d4a550', '&:hover': { bgcolor: 'rgba(212,165,80,0.1)' } }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                )}
                {n.freelancer_id && (
                  <Chip label={t('notifiche.go_profile')} size="small" onClick={e => {
                    e.stopPropagation();
                    window.location.assign(`/freelancer/${n.freelancer_id}`);
                  }} sx={{ cursor: 'pointer', color: '#d4a550', border: '1px solid rgba(212,165,80,0.3)', bgcolor: 'transparent', fontSize: 11 }} />
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
